/**
 * OwnerDashboardService
 * 오너 대시보드 비즈니스 로직
 */
import type {
  SeatStatistics,
  ServiceUsageItem,
  QuickAction,
  QuickActionType,
  DashboardAlert,
  DashboardAlertType,
} from '../models/OwnerDashboard';

/**
 * OwnerDashboardService 클래스
 * 대시보드 관련 비즈니스 로직 및 유틸리티 함수
 */
export class OwnerDashboardService {
  /**
   * 금액 포맷팅 (원화)
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(amount);
  }

  /**
   * 시간 포맷팅 (분 → 시간:분)
   */
  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}시간 ${mins}분` : `${mins}분`;
  }

  /**
   * 현재 시각 포맷팅
   */
  formatCurrentTime(): string {
    return new Date().toLocaleTimeString('ko-KR');
  }

  /**
   * 점유율 색상 클래스 반환
   */
  getOccupancyColorClass(occupancyRate: number): string {
    if (occupancyRate > 90) return 'bg-error-600';
    if (occupancyRate > 70) return 'bg-warning-600';
    return 'bg-success-600';
  }

  /**
   * 대시보드 카드 그라디언트 스타일 반환
   */
  getCardGradientStyle(type: 'primary' | 'success' | 'error' | 'warning'): React.CSSProperties {
    const gradients = {
      primary: 'red',
      success: 'linear-gradient(135deg, #16a34a, #4ade80)',
      error: 'linear-gradient(135deg, #dc2626, #f87171)',
      warning: 'linear-gradient(135deg, #d97706, #fbbf24)',
    };

    return {
      background: gradients[type],
      color: '#ffffff',
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    };
  }

  /**
   * 서비스 사용량 계산
   */
  calculateUsagePercentage(current: number, limit: number): number {
    if (limit === 0) return 0;
    return Math.round((current / limit) * 100);
  }

  /**
   * 서비스 사용량 항목 생성
   */
  getServiceUsageItems(): ServiceUsageItem[] {
    return [
      {
        id: 'sms',
        name: 'SMS 알림',
        icon: 'sms',
        current: 0,
        limit: 100,
        percentage: 0,
      },
      {
        id: 'kakao',
        name: '카카오톡',
        icon: 'kakao',
        current: 0,
        limit: 500,
        percentage: 0,
      },
      {
        id: 'storage',
        name: '저장 용량',
        icon: 'storage',
        current: 0,
        limit: 5,
        percentage: 0,
      },
    ];
  }

  /**
   * 빠른 액션 항목 생성
   */
  getQuickActions(): QuickAction[] {
    return [
      {
        type: 'release_all',
        label: '전체 해제',
        description: '모든 좌석',
        icon: 'XCircleIcon',
        colorClass: 'text-error-600',
        confirmRequired: true,
      },
      {
        type: 'emergency_alert',
        label: '긴급 알림',
        description: '전체 발송',
        icon: 'ExclamationTriangleIcon',
        colorClass: 'text-warning-600',
        confirmRequired: true,
      },
      {
        type: 'system_check',
        label: '시스템 상태',
        description: '점검',
        icon: 'CogIcon',
        colorClass: 'text-primary-600',
        confirmRequired: false,
      },
    ];
  }

  /**
   * 대시보드 알림 생성
   */
  generateDashboardAlert(
    seatStats: SeatStatistics
  ): DashboardAlert {
    if (seatStats.outOfOrder > 0) {
      return {
        type: 'error',
        title: '점검 필요 좌석',
        message: `${seatStats.outOfOrder}개 좌석이 점검 중입니다.`,
        show: true,
      };
    }

    if (seatStats.occupancyRate > 90) {
      return {
        type: 'warning',
        title: '높은 점유율',
        message: `현재 점유율이 ${seatStats.occupancyRate}%로 매우 높습니다.`,
        show: true,
      };
    }

    return {
      type: 'success',
      title: '정상 운영',
      message: '현재 특별한 이슈가 없습니다. 모든 시설이 정상 운영 중입니다.',
      show: true,
    };
  }

  /**
   * 알림 타입에 따른 배지 클래스 반환
   */
  getAlertBadgeClass(type: DashboardAlertType): string {
    switch (type) {
      case 'error':
        return 'status-badge status-error';
      case 'warning':
        return 'status-badge status-warning';
      case 'success':
        return 'status-badge status-success';
      default:
        return 'status-badge';
    }
  }

  /**
   * 긴급 알림 카드 그라디언트 반환
   */
  getEmergencyCardGradient(
    outOfOrder: number,
    occupancyRate: number
  ): string {
    if (outOfOrder > 0 || occupancyRate > 90) {
      return 'linear-gradient(135deg, #dc2626, #f87171)';
    }
    return 'linear-gradient(135deg, #d97706, #fbbf24)';
  }

  /**
   * 긴급 상태 텍스트 반환
   */
  getEmergencyStatusText(
    outOfOrder: number,
    occupancyRate: number
  ): { title: string; status: string; description: string } {
    if (outOfOrder > 0) {
      return {
        title: '긴급 알림',
        status: `${outOfOrder}`,
        description: '점검 필요 좌석',
      };
    }

    if (occupancyRate > 90) {
      return {
        title: '긴급 알림',
        status: '과밀',
        description: '높은 점유율',
      };
    }

    return {
      title: '시스템 상태',
      status: '정상',
      description: '문제 없음',
    };
  }
}

/**
 * OwnerDashboardService 싱글톤 인스턴스
 */
export const ownerDashboardService = new OwnerDashboardService();
